import { useCallback, useEffect, useRef, useState } from "react";
import { X, Maximize2, PanelLeftOpen, PanelLeftClose } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ChatWindow } from "./ChatWindow";
import { ChatSidebar } from "./ChatSidebar";
import { useChatSessions, UIContext } from "@/queries/chat";
import { SignalIcon } from "@/components/icons/SignalIcon";

const STORAGE_KEY = "ai.activeChatSessionId";
const POSITION_KEY = "ai.fabPosition";

// Default bottom-right position (pixels from viewport edges)
const DEFAULT_POSITION = { bottom: 24, right: 24 };
const BTN_SIZE = 56; // h-14 = 56px
const DRAG_THRESHOLD = 4; // px — ignore micro-drags
const EDGE_PADDING = 8; // min distance from viewport edges

// ── Drag zone ─────────────────────────────────────────────
// Defines the rectangular area (from viewport edges) the button can move within.
// Values are in pixels from the respective edge.
const DRAG_ZONE = {
  minRight: 8, // closest to right edge
  maxRight: 100, // farthest from right edge (how far left it can go)
  minBottom: 8, // closest to bottom edge
  maxBottom: 100, // farthest from bottom edge (how far up it can go)
};

/** Persist & restore button position across page navigations */
function getStoredPosition(): { bottom: number; right: number } {
  try {
    const raw = sessionStorage.getItem(POSITION_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    /* ignore */
  }
  return DEFAULT_POSITION;
}

// ---------------------------------------------------------------------------
// FloatingChatButton — compact draggable chat bubble on non-chat pages
// ---------------------------------------------------------------------------
interface FloatingChatButtonProps {
  uiContext?: UIContext;
}

export function FloatingChatButton({
  uiContext,
}: FloatingChatButtonProps = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: sessions } = useChatSessions();

  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  
  // -- REVERT NOTE: Added for expanded view history --
  const [isNewChatMode, setIsNewChatMode] = useState(false);
  const [showHistory, setShowHistory] = useState(true);
  // --------------------------------------------------
  
  const [currentSessionId, setCurrentSessionId] = useState<number | null>(() =>
    getStoredSessionId()
  );

  // ── Drag state ──────────────────────────────────────────
  const [position, setPosition] = useState(getStoredPosition);
  const dragRef = useRef<{
    startX: number;
    startY: number;
    startRight: number;
    startBottom: number;
    moved: boolean;
  } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (e.button !== 0) return; // primary button only
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startRight: position.right,
        startBottom: position.bottom,
        moved: false,
      };
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    },
    [position]
  );

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    // Ignore until past threshold to prevent accidental drags
    if (!d.moved && Math.abs(dx) + Math.abs(dy) < DRAG_THRESHOLD) return;
    d.moved = true;

    // Clamp within the defined drag zone
    const newRight = Math.max(
      DRAG_ZONE.minRight,
      Math.min(DRAG_ZONE.maxRight, d.startRight - dx)
    );
    const newBottom = Math.max(
      DRAG_ZONE.minBottom,
      Math.min(DRAG_ZONE.maxBottom, d.startBottom - dy)
    );
    setPosition({ right: newRight, bottom: newBottom });
  }, []);

  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      const d = dragRef.current;
      dragRef.current = null;
      if (!d) return;
      (e.target as HTMLElement).releasePointerCapture(e.pointerId);

      // Persist position
      sessionStorage.setItem(POSITION_KEY, JSON.stringify(position));

      // If it was just a click (no meaningful drag), toggle chat
      if (!d.moved) {
        setIsOpen((v) => !v);
      }
    },
    [position]
  );

  // Guards against the session list not yet including a just-created session.
  const justCreatedIdRef = useRef<number | null>(null);

  // Re-sync from storage when navigating back from full chat page.
  useEffect(() => {
    if (location.pathname === "/chat") return;
    const stored = getStoredSessionId();
    if (stored !== currentSessionId) {
      setCurrentSessionId(stored);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Validate session against list + fallback to latest.
  useEffect(() => {
    if (!sessions) return;

    if (currentSessionId !== null) {
      const exists = sessions.some((s) => s.id === currentSessionId);
      if (exists) {
        if (justCreatedIdRef.current === currentSessionId) {
          justCreatedIdRef.current = null;
        }
        return;
      }
      // Wait for list to catch up with a just-created session.
      if (justCreatedIdRef.current === currentSessionId) return;
      setCurrentSessionId(null);
      return;
    }

    if (isNewChatMode) return;
    
    // Fallback to latest session.
    const latestId = sessions[0]?.id ?? null;
    setCurrentSessionId(latestId);
    if (latestId) {
      sessionStorage.setItem(STORAGE_KEY, String(latestId));
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }, [sessions, currentSessionId, isNewChatMode]);

  // Hide on full chat page.
  if (location.pathname === "/chat") return null;

  const handleExpand = () => {
    setIsExpanded((prev) => !prev);
  };

  const handleSessionCreated = (id: number) => {
    justCreatedIdRef.current = id;
    setIsNewChatMode(false);
    setCurrentSessionId(id);
    sessionStorage.setItem(STORAGE_KEY, String(id));
  };

  // -- REVERT NOTE: Added for expanded view history --
  const handleNewChat = () => {
    justCreatedIdRef.current = null;
    setIsNewChatMode(true);
    setCurrentSessionId(null);
  };

  const handleSelectSession = (sessionId: number | null) => {
    justCreatedIdRef.current = null;
    setIsNewChatMode(false);
    setCurrentSessionId(sessionId);
  };
  // --------------------------------------------------

  // Chat window — smart flip: open above button if space, otherwise below
  const CHAT_HEIGHT = 600;
  const GAP = 12;
  const btnTopFromViewport =
    typeof window !== "undefined"
      ? window.innerHeight - position.bottom - BTN_SIZE
      : 200;
  const spaceAbove = btnTopFromViewport; // pixels above the button
  const spaceBelow = position.bottom; // pixels below the button
  const opensAbove = spaceAbove >= CHAT_HEIGHT + GAP;
  const opensBelow = !opensAbove && spaceBelow >= CHAT_HEIGHT + GAP;

  // If neither direction fits fully, pick the one with more space and clamp height
  const chatStyle: React.CSSProperties = { right: position.right };
  if (opensAbove) {
    chatStyle.bottom = position.bottom + BTN_SIZE + GAP;
  } else if (opensBelow) {
    chatStyle.bottom = position.bottom - CHAT_HEIGHT - GAP;
  } else {
    // Clamp: use whichever side has more room
    if (spaceAbove >= spaceBelow) {
      chatStyle.top = EDGE_PADDING;
      chatStyle.bottom = position.bottom + BTN_SIZE + GAP;
    } else {
      chatStyle.bottom = EDGE_PADDING;
      chatStyle.top =
        typeof window !== "undefined"
          ? window.innerHeight - position.bottom + GAP
          : 80;
    }
  }

  return (
    <>
      <Button
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        size="icon"
        className="fixed h-14 w-14 rounded-full shadow-lg hover:scale-105 transition-shadow z-50 cursor-grab active:cursor-grabbing select-none touch-none"
        style={{ bottom: position.bottom, right: position.right }}
        title="Open RaidoHub Intelligence — drag to move"
      >
        {isOpen ? (
          <X className="h-6 w-6" />
        ) : (
          <SignalIcon className="h-full w-full rounded-full" />
        )}
      </Button>

      {isOpen && (
        <div
          className={`fixed z-40 flex flex-col overflow-hidden rounded-2xl border border-border/50 bg-background shadow-2xl animate-in fade-in slide-in-from-bottom-2 duration-200 transition-all ${
            isExpanded 
              ? "top-[140px] left-4 md:left-[280px] right-6 bottom-6" 
              : "w-[min(420px,calc(100vw-1.5rem))] h-[600px]"
          }`}
          style={
            isExpanded 
              ? {} // Use Tailwind classes for expanded position
              : {
                  right: position.right,
                  bottom: position.bottom + BTN_SIZE + 8,
                }
          }
        >
          <div className="flex items-center justify-between shrink-0 border-b border-border/40 px-4 py-3 bg-muted/40">
            <div className="flex items-center gap-2">
              <SignalIcon className="h-4 w-4" />
              <span className="text-sm font-semibold">
                RaidoHub Intelligence
              </span>
            </div>
            <div className="flex items-center gap-1">
              {/* REVERT NOTE: Added history toggle button */}
              {isExpanded && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 mr-1"
                  onClick={() => setShowHistory((prev) => !prev)}
                  title={showHistory ? "Hide history" : "Show history"}
                >
                  {showHistory ? (
                    <PanelLeftClose className="h-4 w-4" />
                  ) : (
                    <PanelLeftOpen className="h-4 w-4" />
                  )}
                </Button>
              )}
              {/* -------------------------------------- */}
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={handleExpand}
                title="Toggle full view"
              >
                <Maximize2 className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => setIsOpen(false)}
                title="Close"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>

          <div className="flex flex-1 min-h-0 overflow-hidden">
            {/* REVERT NOTE: Render sidebar when expanded */}
            {isExpanded && showHistory && (
              <div className="hidden md:flex shrink-0 h-full">
                <ChatSidebar
                  currentSessionId={currentSessionId}
                  onSelectSession={handleSelectSession}
                  onNewChat={handleNewChat}
                />
              </div>
            )}
            
            <div className="flex-1 flex flex-col min-w-0">
              <ChatWindow
                sessionId={currentSessionId}
                onSessionCreated={handleSessionCreated}
                isCompact={!isExpanded}
                uiContext={uiContext}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getStoredSessionId(): number | null {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  const num = raw ? Number(raw) : null;
  return num && !isNaN(num) && num > 0 ? num : null;
}
